import { useState, useEffect, useRef, useCallback } from "react";
import { MobileLayout } from "../../components/MobileLayout";
import { useApp } from "../../context/AppContext";
import { supabase } from "../../lib/supabase";
import { audit } from "../../lib/audit";
import {
  Plus, X, AlertTriangle, Camera, Mic, Play, Pause,
  Send, ChevronDown, ChevronUp, Loader2, Trash2, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

const EMPRESA_ID = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";
const ROLES_SUP  = ["supervisor", "admin", "liberty", "esquematiza", "admin_master"];

const CATEGORIAS = ["Segurança", "Equipamento", "Morador", "Visitante", "Operacional", "Outros"];

const PRIOR_CFG = {
  baixa:   { label: "🟢 Baixa",   bg: "#F0FDF4", border: "#BBF7D0", text: "#15803D", dot: "#22C55E" },
  media:   { label: "🟡 Média",   bg: "#FFFBEB", border: "#FDE68A", text: "#92400E", dot: "#F59E0B" },
  alta:    { label: "🔴 Alta",    bg: "#FEF2F2", border: "#FECACA", text: "#991B1B", dot: "#EF4444" },
  critica: { label: "⚫ Crítica", bg: "#18181B", border: "#3F3F46", text: "#FAFAFA", dot: "#52525B" },
};

const STATUS_CFG = {
  pendente:  { label: "Pendente",  bg: "#FFF7ED", text: "#C2410C" },
  aprovada:  { label: "Aprovada",  bg: "#F0FDF4", text: "#15803D" },
  rejeitada: { label: "Rejeitada", bg: "#FEF2F2", text: "#991B1B" },
};

const fmtDT = (iso) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

const fmtTimer = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

function formInicial() {
  return { titulo: "", descricao: "", categoria: "Segurança", prioridade: "media", fotoFile: null, fotoPreview: null };
}

export default function OcorrenciasMobile() {
  const { user } = useApp();
  const isSupervisor = ROLES_SUP.includes(user?.role);
  const nomeUsuario  = user?.usuarioNome || user?.nome || "Usuário";

  // ── Lista ──
  const [ocorrencias,    setOcorrencias]    = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [expandida,      setExpandida]      = useState(null);
  const [comentarios,    setComentarios]    = useState({});
  const [loadingComent,  setLoadingComent]  = useState(null);
  const [textoComent,    setTextoComent]    = useState("");
  const [enviandoComent, setEnviandoComent] = useState(false);

  // ── Formulário ──
  const [modalAberto, setModalAberto] = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [form,        setForm]        = useState(formInicial());
  const fotoRef = useRef(null);

  // ── Áudio ──
  const mediaRecRef = useRef(null);
  const chunksRef   = useRef([]);
  const timerRef    = useRef(null);
  const audioElRef  = useRef(null);
  const [gravando,       setGravando]       = useState(false);
  const [timerSeg,       setTimerSeg]       = useState(0);
  const [audioBlob,      setAudioBlob]      = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
  const [playingPreview, setPlayingPreview] = useState(false);

  // ── Ditado por voz ──
  const [iaModal,    setIaModal]    = useState(false);
  const [ouvindo,    setOuvindo]    = useState(false);
  const [transcript, setTranscript] = useState("");
  const recognitionRef = useRef(null);

  // Limpa timer ao desmontar
  useEffect(() => () => clearInterval(timerRef.current), []);

  // Limpa texto de comentário ao trocar card expandido
  useEffect(() => { setTextoComent(""); }, [expandida]);

  // ── Carregar ocorrências ──
  const carregar = useCallback(async () => {
    let q = supabase
      .from("liberty_ocorrencias")
      .select("*")
      .eq("empresa_id", EMPRESA_ID)
      .order("created_at", { ascending: false });
    if (!isSupervisor) q = q.eq("vigilante_nome", nomeUsuario);
    const { data } = await q;
    setOcorrencias(data || []);
    setLoading(false);
  }, [isSupervisor, nomeUsuario]);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Comentários ──
  const carregarComentarios = async (ocId) => {
    if (comentarios[ocId] !== undefined) return;
    setLoadingComent(ocId);
    const { data } = await supabase
      .from("liberty_ocorrencias_comentarios")
      .select("*")
      .eq("ocorrencia_id", ocId)
      .order("criado_em");
    setComentarios((p) => ({ ...p, [ocId]: data || [] }));
    setLoadingComent(null);
  };

  const toggleExpandir = (ocId) => {
    const next = expandida === ocId ? null : ocId;
    setExpandida(next);
    if (next) carregarComentarios(next);
  };

  const enviarComentario = async (ocId) => {
    if (!textoComent.trim()) return;
    setEnviandoComent(true);
    const { data, error } = await supabase
      .from("liberty_ocorrencias_comentarios")
      .insert({ ocorrencia_id: ocId, usuario_nome: nomeUsuario, texto: textoComent.trim() })
      .select()
      .single();
    if (!error && data) {
      setComentarios((p) => ({ ...p, [ocId]: [...(p[ocId] || []), data] }));
      setTextoComent("");
    }
    setEnviandoComent(false);
  };

  // ── Foto ──
  const handleFoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setForm((f) => ({ ...f, fotoFile: file, fotoPreview: URL.createObjectURL(file) }));
  };

  // ── Áudio ──
  const iniciarGravacao = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      mediaRecRef.current = new MediaRecorder(stream);
      mediaRecRef.current.ondataavailable = (e) => chunksRef.current.push(e.data);
      mediaRecRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioPreviewUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecRef.current.start();
      setGravando(true);
      setTimerSeg(0);
      timerRef.current = setInterval(() => setTimerSeg((s) => s + 1), 1000);
    } catch {
      toast.error("Microfone não disponível neste dispositivo");
    }
  };

  const pararGravacao = () => {
    clearInterval(timerRef.current);
    mediaRecRef.current?.stop();
    setGravando(false);
  };

  const descartarAudio = () => {
    setAudioBlob(null);
    setAudioPreviewUrl(null);
    setTimerSeg(0);
    setPlayingPreview(false);
  };

  const togglePreviewAudio = () => {
    if (!audioElRef.current) return;
    if (playingPreview) { audioElRef.current.pause(); setPlayingPreview(false); }
    else               { audioElRef.current.play();  setPlayingPreview(true);  }
  };

  // ── IA ──
  const iniciarEscuta = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { toast.error("Reconhecimento de voz não suportado neste navegador"); return; }
    const rec = new SpeechRecognition();
    rec.lang = "pt-BR";
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (e) => {
      const texto = Array.from(e.results).map((r) => r[0].transcript).join(" ");
      setTranscript(texto);
    };
    rec.onerror = () => setOuvindo(false);
    rec.onend   = () => setOuvindo(false);
    recognitionRef.current = rec;
    rec.start();
    setOuvindo(true);
    setTranscript("");
  };

  const pararEscuta = () => {
    recognitionRef.current?.stop();
    setOuvindo(false);
  };

  const usarTranscript = () => {
    if (!transcript.trim()) return;
    setForm((f) => ({ ...f, descricao: transcript.trim() }));
    fecharIaModal();
  };

  const fecharIaModal = () => {
    pararEscuta();
    setIaModal(false);
    setTranscript("");
  };

  // ── Salvar ──
  const salvar = async () => {
    if (!form.titulo.trim())   { toast.error("Informe o título");         return; }
    if (!form.descricao.trim()){ toast.error("Descreva a ocorrência");    return; }
    setSaving(true);
    try {
      let foto_url  = null;
      let audio_url = null;

      if (form.fotoFile) {
        const ext  = (form.fotoFile.name.split(".").pop() || "jpg").toLowerCase();
        const path = `${Date.now()}_${nomeUsuario.replace(/\s+/g, "_")}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("ocorrencias-fotos")
          .upload(path, form.fotoFile, { contentType: form.fotoFile.type });
        if (!upErr) {
          const { data: u } = supabase.storage.from("ocorrencias-fotos").getPublicUrl(path);
          foto_url = u?.publicUrl || null;
        }
      }

      if (audioBlob) {
        const path = `${Date.now()}_${nomeUsuario.replace(/\s+/g, "_")}.webm`;
        const { error: auErr } = await supabase.storage
          .from("ocorrencias-audio")
          .upload(path, audioBlob, { contentType: "audio/webm" });
        if (!auErr) {
          const { data: u } = supabase.storage.from("ocorrencias-audio").getPublicUrl(path);
          audio_url = u?.publicUrl || null;
        }
      }

      const now = new Date().toISOString();
      const { error } = await supabase.from("liberty_ocorrencias").insert({
        empresa_id:    EMPRESA_ID,
        titulo:        form.titulo.trim(),
        descricao:     form.descricao.trim(),
        categoria:     form.categoria,
        prioridade:    form.prioridade,
        status:        "pendente",
        foto_url,
        audio_url,
        vigilante_nome: nomeUsuario,
        vigilante_id:   String(user?.usuarioId || user?.id || ""),
        criado_por:     nomeUsuario,
        created_at:     now,
        updated_at:     now,
      });

      if (error) throw error;

      audit({
        usuario_nome: nomeUsuario, usuario_role: user?.role,
        acao: "ocorrencia_registrada", modulo: "Ocorrências",
        descricao: `${nomeUsuario} registrou: ${form.titulo}`,
      });

      toast.success("Ocorrência registrada!");
      fecharModal();
      carregar();
    } catch (err) {
      console.error("[OcorrenciasMobile]", err);
      toast.error("Erro ao salvar. Tente novamente.");
    }
    setSaving(false);
  };

  const fecharModal = () => {
    fecharIaModal();
    setModalAberto(false);
    setForm(formInicial());
    descartarAudio();
    if (gravando) pararGravacao();
  };

  // Métricas
  const total    = ocorrencias.length;
  const pendentes = ocorrencias.filter((o) => o.status === "pendente").length;
  const criticas  = ocorrencias.filter((o) => o.prioridade === "critica" || o.prioridade === "alta").length;

  return (
    <MobileLayout title="Ocorrências" scrollable>
      {/* Summary */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { label: "Total",      value: total,     color: "#0f172a" },
          { label: "Pendentes",  value: pendentes,  color: "#c2410c" },
          { label: "Alta/Crítica",value: criticas,  color: "#991b1b" },
        ].map((m) => (
          <div key={m.label} className="rounded-2xl p-3 text-center" style={{ background: "#ffffff", border: "1px solid #e8eaf0" }}>
            <p className="text-[22px] font-bold leading-none mb-1" style={{ color: m.color }}>{m.value}</p>
            <p className="text-[10px] font-medium" style={{ color: "#94a3b8" }}>{m.label}</p>
          </div>
        ))}
      </div>

      {/* Nova ocorrência */}
      <button
        type="button"
        onClick={() => setModalAberto(true)}
        className="w-full flex items-center justify-center gap-2 py-3.5 mb-4 rounded-2xl font-bold text-[14px] text-white active:scale-[0.98] transition"
        style={{ background: "#08111F" }}
      >
        <Plus className="w-5 h-5" />
        Nova Ocorrência
      </button>

      {/* Lista */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 rounded-2xl animate-pulse" style={{ background: "#e8eaf0" }} />
          ))}
        </div>
      ) : ocorrencias.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-14 text-center">
          <AlertTriangle className="w-10 h-10" style={{ color: "#e8eaf0" }} />
          <p className="text-[14px] font-semibold" style={{ color: "#94a3b8" }}>Nenhuma ocorrência</p>
          <p className="text-[12px]" style={{ color: "#c4c9d6" }}>
            {isSupervisor ? "Nenhuma ocorrência registrada ainda" : "Você não registrou ocorrências"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {ocorrencias.map((oc) => {
            const pCfg  = PRIOR_CFG[oc.prioridade]  || PRIOR_CFG.media;
            const sCfg  = STATUS_CFG[oc.status]      || STATUS_CFG.pendente;
            const isExp = expandida === oc.id;
            const coments = comentarios[oc.id] || [];

            return (
              <div key={oc.id} className="rounded-2xl overflow-hidden" style={{ background: "#fff", border: "1px solid #e8eaf0" }}>
                {/* Cabeçalho clicável */}
                <button
                  type="button"
                  onClick={() => toggleExpandir(oc.id)}
                  className="w-full px-4 py-3.5 flex items-start gap-3 text-left active:bg-[#f8f9fc] transition"
                >
                  <span className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: pCfg.dot }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="font-bold text-[14px] text-[#0f172a] leading-snug">{oc.titulo}</p>
                      <span className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: sCfg.bg, color: sCfg.text }}>
                        {sCfg.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md" style={{ background: pCfg.bg, color: pCfg.text, border: `1px solid ${pCfg.border}` }}>
                        {pCfg.label}
                      </span>
                      <span className="text-[11px]" style={{ color: "#94a3b8" }}>{oc.categoria}</span>
                      <span className="text-[11px]" style={{ color: "#c4c9d6" }}>·</span>
                      <span className="text-[11px]" style={{ color: "#94a3b8" }}>{fmtDT(oc.created_at)}</span>
                      {isSupervisor && oc.vigilante_nome && (
                        <>
                          <span className="text-[11px]" style={{ color: "#c4c9d6" }}>·</span>
                          <span className="text-[11px]" style={{ color: "#94a3b8" }}>{oc.vigilante_nome}</span>
                        </>
                      )}
                    </div>
                  </div>
                  {isExp
                    ? <ChevronUp  className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#94a3b8" }} />
                    : <ChevronDown className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#94a3b8" }} />}
                </button>

                {/* Detalhe expandido */}
                {isExp && (
                  <div style={{ borderTop: "1px solid #e8eaf0" }}>
                    {oc.descricao && (
                      <div className="px-4 py-3">
                        <p className="text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: "#94a3b8" }}>Descrição</p>
                        <p className="text-[13px] leading-relaxed" style={{ color: "#475569" }}>{oc.descricao}</p>
                      </div>
                    )}

                    {oc.foto_url && (
                      <div className="px-4 pb-3">
                        <img src={oc.foto_url} alt="foto" className="w-full rounded-xl object-cover" style={{ maxHeight: 200 }} />
                      </div>
                    )}

                    {oc.audio_url && (
                      <div className="px-4 pb-3">
                        <p className="text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: "#94a3b8" }}>Áudio</p>
                        <audio controls src={oc.audio_url} className="w-full" style={{ height: 36 }} />
                      </div>
                    )}

                    {/* Comentários */}
                    <div className="px-4 pb-4" style={{ borderTop: "1px solid #f1f5f9" }}>
                      <div className="flex items-center gap-2 py-3">
                        <MessageSquare className="w-3.5 h-3.5" style={{ color: "#94a3b8" }} />
                        <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "#94a3b8" }}>
                          Comentários internos ({coments.length})
                        </p>
                      </div>

                      {loadingComent === oc.id ? (
                        <div className="flex justify-center py-4">
                          <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#94a3b8" }} />
                        </div>
                      ) : (
                        <div className="space-y-2 mb-3">
                          {coments.length === 0 && (
                            <p className="text-[12px] text-center py-2 italic" style={{ color: "#c4c9d6" }}>Nenhum comentário ainda</p>
                          )}
                          {coments.map((c) => (
                            <div key={c.id} className="rounded-xl p-3" style={{ background: "#f8f9fc" }}>
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-[12px] font-bold text-[#0f172a]">{c.usuario_nome}</p>
                                <p className="text-[10px]" style={{ color: "#c4c9d6" }}>{fmtDT(c.criado_em)}</p>
                              </div>
                              <p className="text-[13px]" style={{ color: "#475569" }}>{c.texto}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={textoComent}
                          onChange={(e) => setTextoComent(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviarComentario(oc.id); } }}
                          placeholder="Comentar internamente..."
                          className="flex-1 px-3 py-2.5 rounded-xl text-[13px] focus:outline-none"
                          style={{ background: "#f1f5f9", color: "#0f172a" }}
                        />
                        <button
                          type="button"
                          onClick={() => enviarComentario(oc.id)}
                          disabled={enviandoComent || !textoComent.trim()}
                          className="w-10 h-10 rounded-xl flex items-center justify-center disabled:opacity-40"
                          style={{ background: "#08111F" }}
                        >
                          {enviandoComent
                            ? <Loader2 className="w-4 h-4 text-white animate-spin" />
                            : <Send className="w-4 h-4 text-white" />}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modal Nova Ocorrência ── */}
      {modalAberto && (
        <div
          className="fixed inset-0 z-[999] flex items-end justify-center"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          onClick={fecharModal}
        >
          <div
            className="w-full max-w-md rounded-t-3xl flex flex-col"
            style={{ background: "#ffffff", maxHeight: "92dvh" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex-none px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid #e8eaf0" }}>
              <p className="font-bold text-[15px] text-[#0f172a]">Nova Ocorrência</p>
              <button type="button" onClick={fecharModal} className="w-8 h-8 flex items-center justify-center rounded-xl" style={{ background: "#f1f5f9" }}>
                <X className="w-4 h-4 text-[#64748b]" />
              </button>
            </div>

            {/* Conteúdo com scroll */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">

              {/* Ditar por voz */}
              <button
                type="button"
                onClick={() => setIaModal(true)}
                className="w-full py-3 rounded-2xl font-bold text-[13px] flex items-center justify-center gap-2 active:scale-[0.98] transition"
                style={{ background: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0" }}
              >
                <Mic className="w-4 h-4" style={{ color: "#475569" }} />
                Ditar por voz
              </button>

              {/* Prioridade */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: "#64748b" }}>Prioridade</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(PRIOR_CFG).map(([key, cfg]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, prioridade: key }))}
                      className="py-2.5 rounded-xl text-[12px] font-bold transition"
                      style={
                        form.prioridade === key
                          ? { background: cfg.bg, border: `2px solid ${cfg.dot}`, color: cfg.text }
                          : { background: "#f1f5f9", border: "2px solid transparent", color: "#64748b" }
                      }
                    >
                      {cfg.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Categoria */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: "#64748b" }}>Categoria</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIAS.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, categoria: cat }))}
                      className="px-3 py-2 rounded-xl text-[12px] font-semibold transition"
                      style={
                        form.categoria === cat
                          ? { background: "#08111F", color: "#ffffff" }
                          : { background: "#f1f5f9", color: "#64748b" }
                      }
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Título */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#64748b" }}>Título *</label>
                <input
                  type="text"
                  value={form.titulo}
                  onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
                  placeholder="Ex: Veículo suspeito na garagem"
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl text-[14px] bg-white text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none"
                  style={{ border: "1px solid #e8eaf0" }}
                />
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#64748b" }}>Descrição *</label>
                <textarea
                  value={form.descricao}
                  onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                  placeholder="O que aconteceu, quem foi acionado e qual providência foi tomada..."
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl text-[14px] bg-white text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none resize-none"
                  style={{ border: "1px solid #e8eaf0" }}
                />
              </div>

              {/* Foto — câmera apenas */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#64748b" }}>Foto (opcional)</label>
                <input ref={fotoRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFoto} />
                {form.fotoPreview ? (
                  <div className="relative">
                    <img src={form.fotoPreview} alt="preview" className="w-full rounded-xl object-cover" style={{ maxHeight: 180 }} />
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, fotoFile: null, fotoPreview: null }))}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center"
                    >
                      <X className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fotoRef.current?.click()}
                    className="w-full py-3.5 rounded-xl flex items-center justify-center gap-2 text-[13px] font-semibold transition active:scale-[0.98]"
                    style={{ background: "#f1f5f9", border: "1px dashed #cbd5e1", color: "#64748b" }}
                  >
                    <Camera className="w-4 h-4" />
                    Tirar foto
                  </button>
                )}
              </div>

              {/* Áudio */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#64748b" }}>Áudio (opcional)</label>

                {audioBlob ? (
                  <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                    <button
                      type="button"
                      onClick={togglePreviewAudio}
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: "#22c55e" }}
                    >
                      {playingPreview ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white" />}
                    </button>
                    <div className="flex-1">
                      <p className="text-[12px] font-semibold" style={{ color: "#15803d" }}>Áudio gravado</p>
                      <p className="text-[11px] font-mono" style={{ color: "#86efac" }}>{fmtTimer(timerSeg)}</p>
                    </div>
                    <button type="button" onClick={descartarAudio} className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "#fee2e2" }}>
                      <Trash2 className="w-3.5 h-3.5 text-red-600" />
                    </button>
                    <audio ref={audioElRef} src={audioPreviewUrl} onEnded={() => setPlayingPreview(false)} className="hidden" />
                  </div>
                ) : gravando ? (
                  <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: "#fef2f2", border: "1px solid #fecaca" }}>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse" style={{ background: "#ef4444" }}>
                      <Mic className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[12px] font-semibold text-red-700">Gravando…</p>
                      <p className="text-[11px] font-mono text-red-400">{fmtTimer(timerSeg)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={pararGravacao}
                      className="px-3 py-1.5 rounded-xl text-[12px] font-bold text-white"
                      style={{ background: "#ef4444" }}
                    >
                      Parar
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={iniciarGravacao}
                    className="w-full py-3.5 rounded-xl flex items-center justify-center gap-2 text-[13px] font-semibold transition active:scale-[0.98]"
                    style={{ background: "#f1f5f9", border: "1px dashed #cbd5e1", color: "#64748b" }}
                  >
                    <Mic className="w-4 h-4" />
                    Gravar áudio
                  </button>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex-none px-5 py-4 flex gap-2" style={{ borderTop: "1px solid #e8eaf0" }}>
              <button
                type="button"
                onClick={fecharModal}
                className="flex-none px-5 py-3 rounded-2xl text-sm font-semibold"
                style={{ background: "#f1f5f9", color: "#64748b" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={salvar}
                disabled={saving || !form.titulo.trim() || !form.descricao.trim()}
                className="flex-1 py-3 rounded-2xl text-white font-bold text-sm active:scale-[0.98] transition disabled:opacity-40 flex items-center justify-center gap-2"
                style={{ background: "#08111F" }}
              >
                {saving
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <AlertTriangle className="w-4 h-4" />}
                {saving ? "Registrando…" : "Registrar Ocorrência"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Modal Ditar por voz ── */}
      {iaModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-end justify-center"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          onClick={fecharIaModal}
        >
          <div
            className="w-full max-w-md rounded-t-3xl flex flex-col"
            style={{ background: "#ffffff", maxHeight: "75dvh" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex-none px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid #e8eaf0" }}>
              <div className="flex items-center gap-2">
                <Mic className="w-4 h-4" style={{ color: "#475569" }} />
                <p className="font-bold text-[15px] text-[#0f172a]">Ditar por voz</p>
              </div>
              <button type="button" onClick={fecharIaModal} className="w-8 h-8 flex items-center justify-center rounded-xl" style={{ background: "#f1f5f9" }}>
                <X className="w-4 h-4 text-[#64748b]" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <p className="text-[13px] text-center" style={{ color: "#64748b" }}>
                Fale a descrição da ocorrência. O texto vai direto para o campo Descrição.
              </p>

              {/* Botão microfone */}
              <div className="flex flex-col items-center gap-3 py-2">
                <button
                  type="button"
                  onClick={ouvindo ? pararEscuta : iniciarEscuta}
                  className="w-20 h-20 rounded-full flex items-center justify-center transition active:scale-95"
                  style={{ background: ouvindo ? "#ef4444" : "#1e293b" }}
                >
                  <Mic className={`w-8 h-8 text-white ${ouvindo ? "animate-pulse" : ""}`} />
                </button>
                <p className="text-[12px] font-semibold" style={{ color: ouvindo ? "#ef4444" : "#94a3b8" }}>
                  {ouvindo ? "Ouvindo… toque para parar" : "Toque para falar"}
                </p>
              </div>

              {/* Texto transcrito */}
              {transcript ? (
                <div className="rounded-xl p-3" style={{ background: "#f8f9fc", border: "1px solid #e2e8f0" }}>
                  <p className="text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: "#94a3b8" }}>Texto reconhecido</p>
                  <p className="text-[14px] leading-relaxed" style={{ color: "#1e293b" }}>{transcript}</p>
                </div>
              ) : null}
            </div>

            {/* Footer */}
            <div className="flex-none px-5 py-4 flex gap-2" style={{ borderTop: "1px solid #e8eaf0" }}>
              <button
                type="button"
                onClick={() => setTranscript("")}
                disabled={!transcript}
                className="px-4 py-3 rounded-2xl text-[13px] font-semibold disabled:opacity-30"
                style={{ background: "#f1f5f9", color: "#64748b" }}
              >
                Limpar
              </button>
              <button
                type="button"
                onClick={usarTranscript}
                disabled={!transcript.trim() || ouvindo}
                className="flex-1 py-3 rounded-2xl text-white font-bold text-[14px] flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition"
                style={{ background: "#08111F" }}
              >
                Usar texto
              </button>
            </div>
          </div>
        </div>
      )}
    </MobileLayout>
  );
}
